import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountCard, ButtonLink, Card, DeductionCard, EmptyState, ExplainerBanner, PhotoAttach, Sheet, Skeleton, StatusChip, Textarea, Timeline } from '@/ds'
import type { AttachedPhoto } from '@/ds'
import { dayMonth, daysUntil, formatMoney, formatMoneyShort } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { fianzaService } from '@/services/fianza'
import type { Deposit } from '@/services/fianza'
import type { Stay } from '@/services/reserva'
import { useApiLang, useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { PAY_ROUTES, usePagosStay, OriginBackLink } from '../Pagos/shared'
import { explainerFor, summaryFor, timelineItems } from './copy'

function leadFor(d: Deposit | null, stay: Stay, lang: string, t: TFunction): string | null {
  if (!d) return null
  switch (d.status) {
    case 'pending':
      return t('v2.fianza.lead.pending', { name: stay.unit.name || stay.property.name, amount: formatMoneyShort(d.amount, lang) })
    case 'held':
      return t('v2.fianza.lead.held', { name: stay.unit.name || stay.property.name, amount: formatMoneyShort(d.amount, lang) })
    case 'review':
      return t('v2.fianza.lead.review')
    case 'deducted':
      return t('v2.fianza.lead.deducted')
    default:
      return t('v2.fianza.lead.released')
  }
}

/** "No estoy de acuerdo · reclamar" (Bloque 3 §3e): texto y fotos a la propiedad. Sin endpoint (mock). */
function ClaimSheet({ stay, open, onClose, onSent }: { stay: Stay; open: boolean; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [photos, setPhotos] = useState<(AttachedPhoto & { data: string })[]>([])
  const [missing, setMissing] = useState(false)
  const { saving, error, run } = useSave()
  const send = async () => {
    if (!text.trim()) return setMissing(true)
    const ok = await run(async () => {
      await fianzaService.claim(stay, { text: text.trim(), photos: photos.map((p) => p.data) })
      return true
    })
    if (ok) onSent()
  }
  const add = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => setPhotos((list) => [...list, { id: `${Date.now()}`, url: URL.createObjectURL(file), label: file.name, data: typeof reader.result === 'string' ? reader.result : '' }])
    reader.readAsDataURL(file)
  }
  return (
    <Sheet open={open} title={t('v2.fianza.reclamar.titulo')} confirmLabel={t('v2.fianza.reclamar.enviar')} onConfirm={() => void send()} onCancel={onClose} loading={saving}>
      <div className="pagos__group">
        <Textarea label={t('v2.fianza.reclamar.campo')} value={text} onChange={(e) => {
          setText(e.target.value)
          setMissing(false)
        }} error={missing ? t('v2.legacy.validations.Required') : undefined} rows={4} />
        <PhotoAttach label={t('v2.fianza.reclamar.fotos')} labelNote={t('v2.fianza.reclamar.fotosNota')} photos={photos} onAdd={add} onRemove={(id) => setPhotos((list) => list.filter((p) => p.id !== id))} />
        {error && (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Sheet>
  )
}

function DepositView({ stay, deposit, onChanged }: { stay: Stay; deposit: Deposit; onChanged: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { go } = useAppNavigate()
  const [claiming, setClaiming] = useState(false)
  const summary = summaryFor(deposit, lang, t)
  const ded = deposit.deduction
  const explainer = explainerFor(deposit, t)
  const days = ded?.claimDeadline ? Math.max(0, daysUntil(ded.claimDeadline)) : null
  return (
    <>
      <AmountCard
        total={summary.amount}
        totalLabel={summary.label}
        chip={<StatusChip tone={summary.chip.tone} icon="info">{summary.chip.text}</StatusChip>}
        note={summary.text}
        buttonLabel={deposit.status === 'pending' ? t('v2.fianza.reservar.boton', { amount: formatMoney(deposit.amount, lang) }) : null}
        onPay={() => go(PAY_ROUTES.reserveDeposit)}
      />
      {deposit.timeline.length > 0 && (
        <Card as="section" aria-label={t('v2.fianza.seguimiento')}>
          <Timeline items={timelineItems(deposit, lang, t)} />
        </Card>
      )}
      {ded && (
        <DeductionCard
          reason={<h2 className="pagos__h3">{t('v2.fianza.deduccion.motivo', { reason: ded.reason })}</h2>}
          photos={ded.photos.map((url, i) => ({ id: String(i), ...(url ? { url } : {}), alt: t('v2.fianza.deduccion.foto', { n: i + 1, total: ded.photos.length }) }))}
          lines={[{ label: t('v2.fianza.deduccion.retenida'), amount: formatMoney(deposit.amount, lang) }, ...ded.breakdown.map((b) => ({ label: b.concept, amount: formatMoney(b.amount, lang).replace('-', '−') }))]}
          totalLabel={t('v2.fianza.deduccion.seLibera')}
          total={formatMoney(ded.released, lang)}
          claimLabel={t('v2.fianza.deduccion.reclamar')}
          {...(deposit.claim ? {} : { onClaim: () => setClaiming(true) })}
          claimNote={
            deposit.claim ? (
              <StatusChip tone="success">{t('v2.fianza.deduccion.reclamacionEnviada', { date: dayMonth(deposit.claim.submittedAt, lang) })}</StatusChip>
            ) : days !== null ? (
              t('v2.fianza.deduccion.plazo', { count: days })
            ) : undefined
          }
        />
      )}
      {explainer && <ExplainerBanner title={explainer.title}>{explainer.text}</ExplainerBanner>}
      {stay.contact.whatsapp && (
        <ButtonLink variant="secondary" icon="message-circle" href={whatsappHref(stay.contact.whatsapp, stay.reference)} target="_blank" rel="noreferrer">
          {t('v2.ds.contact.whatsapp')}
        </ButtonLink>
      )}
      <ClaimSheet
        stay={stay}
        open={claiming}
        onClose={() => setClaiming(false)}
        onSent={() => {
          setClaiming(false)
          onChanged()
        }}
      />
    </>
  )
}

/**
 * 8.3 · Tu fianza (Pagos.dc.html held/review/released/deduction, PantallaFianza.dc.html, Bloque 3 §3b–3e).
 * Real: importe, pendiente/retenida/devuelta y tres hitos (E2, D7). Mock: revisión con plazo, ventana
 * "disponible entre", deducción y reclamación (docs/backend/fianza.md).
 */
export function Fianza() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = usePagosStay()
  const [deposit, reload] = useAsync(`fianza:${stay.guestCode}:${apiLang}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const d = deposit.kind === 'ready' ? deposit.value : null
  const lead = leadFor(d, stay, lang, t)
  return (
    <main className="page pagos">
      <div className="pagos__head">
        <OriginBackLink />
        <h1>{t('v2.fianza.titulo')}</h1>
        {lead && <p className="page__lead">{lead}</p>}
      </div>
      {deposit.kind === 'loading' && (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={180} />
          <Skeleton height={320} />
        </div>
      )}
      {deposit.kind === 'error' && <SectionError code={deposit.code} onRetry={reload} />}
      {deposit.kind === 'ready' && !d && (
        <EmptyState icon="wallet" title={t('v2.fianza.sinFianza')} headingLevel={2}>
          {t('v2.fianza.sinFianzaTexto')}
        </EmptyState>
      )}
      {d && <DepositView stay={stay} deposit={d} onChanged={reload} />}
    </main>
  )
}
